/* eslint-disable func-names */
import {
    takeLatest,
    take,
    put,
    call,
    select,
    join,
    fork,
    spawn,
    takeEvery,
    all,
    race,
} from 'redux-saga/effects';
import ClapiApi from '../api/clapi';
import RapidSearchConst from '../constants/rapidSearch';
import * as TYPE from '../actions/clapi';
import * as RapidAction from '../actions/rapid';
import {
    addCruxCmptError,
    CRUX_APP_ERROR,
    cruxAppError,
    invalidateCruxAppError,
    rmCruxCmptError,
} from '../actions/errorHandler';
import Bugsnag from '../bugsnag';
import RapidLocalisationHelper from '../helpers/search/RapidLocalisationHelper';
import Commons from '../helpers/Commons';
import { getPropertyByID } from './rapid';
import ErrorMsg from '../constants/errorMsg';
import { ErrorType } from '../constants/redirect';
import getSearchFiltersLimit from '../selectors/getSearchFiltersLimit';
import { getSavedListInfoMultiple } from '../actions/watchList';
import {
    FETCH_DYNAMIC_PROPERTY_BY_IDS,
    GET_PROPERTY_BY_COORDINATES_FAILED,
    GET_PROPERTY_BY_COORDINATES_SUCCESS,
    SET_DYNAMIC_COORDINATES,
} from '../actions/search';
import { trackEvent } from '../actions/segment';
import Segment from '../helpers/Segment';
import MapHelper from '../helpers/MapHelper';

export const getTargetProperty = store => store.rapid.get('targetProperty').value;
export const isDynamicCoordinatesNull = (action) =>
    action.type === SET_DYNAMIC_COORDINATES && action.payload === null;

// GET SEARCH RESULT
function getLocationSearchResultsFn() {
    return function* (successType = TYPE.GET_SEARCH_RESULTS_SUCCESS,
                      failType = TYPE.GET_SEARCH_RESULTS_FAIL,
                      propertyId) {
        try {
            const state = yield select();
            let searchResults;
            const searchFilters = state.searchResults.get('searchFilters');
            const statusType = state.searchResults.get('statusType');
            const offset = state.searchResults.get('pageLimitOffset');
            const { lat, lon, suburbOnly } = searchFilters || {};

            let localityParams = {};
            if (lat && lon) {
                const prevTargetProperty = getTargetProperty(state);
                let targetPropertyTask;
                if (!prevTargetProperty) {
                    yield put({
                        type: RapidAction.GET_PROPERTY_BY_ID_FORKED,
                    });
                    targetPropertyTask = yield fork(
                        getPropertyByID,
                        {
                            payload: {
                                propertyId,
                            },
                        },
                    );
                    if (suburbOnly && targetPropertyTask) {
                        yield join(targetPropertyTask);
                    }
                }
                if (suburbOnly) {
                    const targetPropertyDetails = yield select(getTargetProperty);
                    localityParams = RapidLocalisationHelper
                        .getRapidLocalityParamObj(targetPropertyDetails);
                }
            }
            const limit = getSearchFiltersLimit(state);
            searchResults = yield call(
                ClapiApi.getBatchSearch,
                {
                    ...searchFilters,
                    statusType,
                    ...localityParams,
                    ...(limit ? { limit } : {}),
                    ...(offset ? { offset } : {}),
                },
            );
            yield put(TYPE.clearPageLimitOffset());

            // For radius search: Removing target property that is included in the return,
            // it should not display in results table.
            if (propertyId && searchResults.data) {
                const {
                    metadata,
                    data,
                } = searchResults || {};

                const prevSearchResults = state.searchResults.get('searchResults');
                const indexOfTargetProperty = data
                    .findIndex(list => list.id === parseInt(propertyId, 10));
                // Removing target property
                if (indexOfTargetProperty !== null && indexOfTargetProperty > -1) {
                    data.splice(indexOfTargetProperty, 1);
                    // subtracting target property count to totalElements count
                    metadata.totalElements -= 1;
                } else if (metadata.offset > 0 && prevSearchResults && prevSearchResults.metadata) {
                    // equating the previous total element less the target property.
                    // Showing more search results will update the total element count to its
                    // true value with the target property count.
                    // Should be implemented on pages other that 0 only.
                    metadata.totalElements = prevSearchResults.metadata.totalElements;
                }
                // after target property is removed and data length is empty,
                // that means radius inputted is too certain that the target
                // property is the only returned property.
                // Making searchResults into empty object will display in UI a no result field.
                if (data.length === 0) {
                    searchResults = {};
                }
            }

            // Call propertySummary when data is available
            if (searchResults.data) {
                const propertyIds = searchResults.data.map(({ id }) => id);
                yield put(
                    // eslint-disable-next-line no-use-before-define
                    TYPE.getPropertyDetailsByIds(
                        propertyIds,
                        RapidSearchConst.SUMMARY_RETURN_FIELDS,
                    ),
                );

                if (propertyIds?.length) {
                    // This will not proceed if not entitled
                    // Drill down the saga implementation to see the logic behind.
                    yield put(getSavedListInfoMultiple(propertyIds));
                }
            }

            yield put({
                type: successType,
                payload: searchResults,
            });

            yield put(invalidateCruxAppError());
        } catch (error) {
            if (error.status === 404) {
                yield put({
                    type: successType,
                    payload: {},
                });
            } else {
                Bugsnag.notify(error);
                yield put(cruxAppError(ErrorMsg.UNAVAILABLE, ErrorType.PAGE_ERROR));
                yield put({ type: failType });
            }
        }
    };
}
export const getLocationSearchResults = getLocationSearchResultsFn();
export function* getLocationSearchResultsWatcher() {
    yield takeLatest(TYPE.GET_SEARCH_RESULTS, getLocationSearchResults);
}

function getRapidSearchResultFn() {
    return function* (successType = TYPE.GET_RAPID_NAME_SEARCH_RESULT_SUCCESS,
                      failType = TYPE.GET_RAPID_NAME_SEARCH_RESULT_FAIL) {
        try {
            // select ids from state
            const state = yield select();
            const searchFilters = state.searchResults.get('searchFilters');
            const statusType = state.searchResults.get('statusType');
            const offset = state.searchResults.get('pageLimitOffset');
            const limit = getSearchFiltersLimit(state);
            // Per-source offsets for multi-source Show More. Cleared by the reducer on every
            // SET_SEARCH_FILTERS_V2 dispatch (new shape / filter change), so non-Show-More
            // searches always receive an empty array and no offset override is applied.
            const pagingSourceOffsets = state.searchResults.get('pagingSourceOffsets');
            const response = yield call(
                ClapiApi.getBatchSearch,
                {
                    statusType,
                    ...searchFilters,
                    ...(limit ? { limit } : {}),
                    ...(offset ? { offset } : {}),
                },
                false,                        // includeGooglePhotos
                pagingSourceOffsets || [],    // per-source offsets; [] = no override
            );
            yield put(TYPE.clearPageLimitOffset());

            // Call propertySummary when data is available
            if (Commons.get(response, 'data.length')) {
                const propertyIds = response.data.map(({ id }) => id);
                yield put(
                    // eslint-disable-next-line no-use-before-define
                    TYPE.getPropertyDetailsByIds(
                        propertyIds,
                        RapidSearchConst.SUMMARY_RETURN_FIELDS,
                    ),
                );

                if (propertyIds?.length) {
                    // This will not proceed if not entitled
                    // Drill down the saga implementation to see the logic behind.
                    yield put(getSavedListInfoMultiple(propertyIds));
                }
            }

            yield put({
                type: successType,
                payload: response,
            });
            yield put(invalidateCruxAppError());
        } catch (error) {
            Bugsnag.notify(error);
            yield put(cruxAppError(ErrorMsg.UNAVAILABLE, ErrorType.PAGE_ERROR));
            yield put({ type: failType });
        }
    };
}
export const getRapidSearchResult = getRapidSearchResultFn();
export function* getRapidSearchResultWatcher() {
    yield takeLatest(
        TYPE.GET_RAPID_NAME_SEARCH_RESULT,
        getRapidSearchResult,
        TYPE.GET_RAPID_NAME_SEARCH_RESULT_SUCCESS,
        TYPE.GET_RAPID_NAME_SEARCH_RESULT_FAIL,
    );
}

// SET SEARCH FILTERS V2
function setSearchFiltersV2Fn() {
    return function* (successType,
                      failType,
                      action) {
        try {
            if (action.payload.isNonAddressSearch) {
                yield spawn(getRapidSearchResult, successType, failType);
            } else {
                yield spawn(getLocationSearchResults, successType, failType, action.payload.propertyId);
            }
        } catch (error) {
            Bugsnag.notify(error);
            yield put({ type: CRUX_APP_ERROR, error });
        }
    };
}
export const setSearchFiltersV2 = setSearchFiltersV2Fn();
export function* setSearchFiltersV2Watcher() {
    yield takeLatest(TYPE.SET_SEARCH_FILTERS_V2, setSearchFiltersV2, undefined, undefined);
}

export function* setSearchFilterLimitWatcher() {
    yield takeLatest(TYPE.UPDATE_SEARCH_FILTERS_LIMIT, setSearchFiltersV2, undefined, undefined);
}

export function* updateSearchFiltersOffsetWatcher() {
    yield takeLatest(
        TYPE.UPDATE_SEARCH_FILTERS_OFFSET,
        setSearchFiltersV2,
        TYPE.SHOW_MORE_SUCCESS,
        undefined,
    );
}

// GET PROPERTY DETAILS BY PROPERTY ID
function getPropertyDetailsByIdsFn() {
    return function* (successType, failType, action) {
        try {
            if (action.payload.length !== 0) {
                const ids = [];
                for (let i = 0; i < action.payload.length; i += 50) {
                    ids.push(action.payload.slice(i, i + 50));
                }
                const results = yield all(ids.map((_ids) => call(
                    ClapiApi.getPropertyDetailsByIds,
                    _ids,
                    action.returnFields,
                )));

                const searchResults = results.reduce((responses, response) => {
                    if (response?.result?.propertySummaryList) {
                        responses.result.propertySummaryList.push(...response.result.propertySummaryList);
                    }
                    return responses;
                }, { result: { propertySummaryList: [] } });

                yield put({
                    type: successType,
                    payload: searchResults.result,
                });
                yield put(rmCruxCmptError(failType));
            } else {
                yield put(addCruxCmptError(
                    failType,
                    { noResults: true },
                ));
            }
        } catch (error) {
            Bugsnag.notify(error);
            // address search sale details fetching failed
            const failedPropertyIds = [];
            action.payload.forEach((id) => {
                failedPropertyIds.push({
                    propertyId: id,
                    saleDetailsFetchingFailed: true,
                    errors: [new Error('error')], // this will serve as placeholder for errors
                });
            });
            yield put({
                type: failType,
                payload: failedPropertyIds,
            });
        }
    };
}
export const getPropertyDetailsByIds = getPropertyDetailsByIdsFn();
export function* getPropertyDetailsByIdsWatcher() {
    yield takeEvery(
        TYPE.GET_PROPERTY_DETAILS_BY_IDS,
        getPropertyDetailsByIds,
        TYPE.GET_PROPERTY_DETAILS_BY_IDS_SUCCESS,
        TYPE.GET_PROPERTY_DETAILS_BY_IDS_FAIL,
    );
}

// FETCH DYNAMIC PROPERTY DETAILS BY IDS
export function* fetchDynamicPropertyByIdsSaga(action) {
    try {
        const { propertyIds, mixpanel, isRadiusSearch } = action.payload;

        if (!propertyIds?.length) {
            yield put({ type: GET_PROPERTY_BY_COORDINATES_SUCCESS, payload: [{ noResults: true }] });
            yield put(rmCruxCmptError(action.type));
            return;
        }

        const { success: results } = yield race({
            success: call(ClapiApi.getBatchSearch, { propertyIds, limit: 200 }, true),
            cancel: take(isDynamicCoordinatesNull),
        });

        if (!results) return;

        const propertyPhotosMap = {};
        if (results?.googlePhotos) {
            results.googlePhotos.forEach(photoArray => {
                const streetPhotos = photoArray.filter(photo => photo.type === 'street');
                if (streetPhotos.length > 0) {
                    const propertyId = streetPhotos[0].id;
                    propertyPhotosMap[propertyId] = streetPhotos;
                }
            });
        }

        const processProperty = (property) => {
            const streetPhotos = propertyPhotosMap[property.id] || [];
            const streetPhotoUrl = streetPhotos.length > 0 ? streetPhotos[0].mediumPhotoUrl : null;
            return {
                ...property,
                imageUrls: {
                    ...property.imageUrls,
                    mediumImageUrl: property.imageUrls?.mediumImageUrl || streetPhotoUrl,
                },
            };
        };

        let properties;
        if (isRadiusSearch) {
            const state = yield select();
            const { lat, lon } = state.searchResults.get('searchFilters');
            properties = results.data.map(property => {
                const processedProperty = processProperty(property);
                return {
                    ...processedProperty,
                    distance: MapHelper.getDistanceFromTarget(
                        { lat, lon },
                        property.addressLocation,
                    ),
                };
            });
        } else {
            properties = results.data.map(processProperty);
        }

        if (mixpanel) {
            const mixpanelPayload = {
                ...mixpanel,
                objectContext: properties?.length > 1 ? 'Multiple' : 'Single',
            };
            yield put(trackEvent(Segment.viewPropertyBubbleMap(mixpanelPayload)));
        }

        yield put({ type: GET_PROPERTY_BY_COORDINATES_SUCCESS, payload: properties });
        yield put(rmCruxCmptError(action.type));
    } catch (error) {
        Bugsnag.notify({
            name: 'Failed to fetch dynamic property details.',
            message: `propertyIds: ${JSON.stringify(action.payload?.propertyIds)}: ${error.message}`,
        });
        yield put(addCruxCmptError(action.type));
        yield put({ type: GET_PROPERTY_BY_COORDINATES_FAILED });
    }
}

export function* fetchDynamicPropertyByIdsWatcher() {
    yield takeEvery(FETCH_DYNAMIC_PROPERTY_BY_IDS, fetchDynamicPropertyByIdsSaga);
}

export default [
    getPropertyDetailsByIdsWatcher(),
    setSearchFiltersV2Watcher(),
    setSearchFilterLimitWatcher(),
    getLocationSearchResultsWatcher(),
    updateSearchFiltersOffsetWatcher(),
    getRapidSearchResultWatcher(),
    fetchDynamicPropertyByIdsWatcher(),
];
